import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import heroImage from "@/assets/lifestyle-hero.webp";
import imgChauffeur from "@/assets/lifestyle-chauffeur.webp";
import LifestyleOpening from "@/components/lifestyle/LifestyleOpening";
import LifestylePhilosophy from "@/components/lifestyle/LifestylePhilosophy";
import LifestyleExperience from "@/components/lifestyle/LifestyleExperience";
import LifestyleServices from "@/components/lifestyle/LifestyleServices";
import LifestyleClosing from "@/components/lifestyle/LifestyleClosing";

const ease = [0.22, 1, 0.36, 1] as const;

const destinations = [
  { label: "Ibiza", path: "/ibiza/lifestyle" },
  { label: "Marbella", path: "/marbella/lifestyle" },
  { label: "Saint-Tropez", path: "/saint-tropez/lifestyle" },
  { label: "Dubai", path: "/dubai#services" },
  { label: "Paris", path: "/paris/lifestyle" },
];

const faqs = [
  { q: "What services does Arzani Lifestyle include?", a: "Our lifestyle services cover private transport, VIP chauffeur, dining reservations, nightlife access, wellness treatments, private chefs, security, and bespoke requests." },
  { q: "Is Arzani Lifestyle available in all destinations?", a: "We currently operate across Ibiza, Saint-Tropez, Dubai, Marbella, and Paris. Services may vary by location." },
  { q: "How far in advance should I book?", a: "We recommend booking at least one week in advance for peak season. However, we always aim to accommodate last-minute requests." },
  { q: "Can I combine Lifestyle with other Arzani services?", a: "Absolutely. Most clients combine Lifestyle with Yachts, Drive, or Residences for a fully arranged experience." },
  { q: "Is there a minimum booking period?", a: "No. Our services can be arranged for a single day or an extended stay, depending on your needs." },
  { q: "How does communication work?", a: "You will have a single point of contact available via WhatsApp, phone, or email throughout your stay." },
  { q: "Can you arrange services for a group?", a: "Yes. We regularly coordinate lifestyle services for families, groups of friends, and corporate clients." },
  { q: "Are wellness treatments available at my villa?", a: "Yes. We arrange in-villa massage, beauty treatments, and private wellness sessions at your residence." },
  { q: "Can you arrange last-minute restaurant reservations?", a: "We will always do our best. Our network and relationships allow us to secure priority access, even on short notice." },
  { q: "What happens if my plans change?", a: "Our team adapts in real time. We adjust reservations, transport, and arrangements as your schedule evolves." },
];

const Lifestyle = () => {
  const navigate = useNavigate();

  const handleDestinationClick = (dest: typeof destinations[0]) => {
    if (dest.path) {
      navigate(dest.path);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Lifestyle — Reservations, Transport & Bespoke Arrangements"
        description="Chauffeur, dining, nightlife, wellness and bespoke requests carefully arranged across all our destinations."
        path="/lifestyle"
      />
      <Navigation />

      {/* Sticky Enquiry Button */}
      <a
        href="https://wa.me/message/2XEKJ5STR2IZJ1"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 z-50 px-8 py-3.5 bg-foreground text-background font-label tracking-[0.2em] text-[10px] uppercase shadow-lg hover:bg-foreground/90 transition-all duration-500"
      >
        Make Your Enquiry
      </a>

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Lifestyle" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Personal Services · Worldwide
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Lifestyle
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Lifestyle is not defined by what you have access to, but by how effortlessly it comes together.
          </motion.p>
        </div>
      </section>

      {/* Destinations Bar */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block text-center mb-10">
              Our Destinations
            </span>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {destinations.map((dest) => (
                <button
                  key={dest.label}
                  onClick={() => handleDestinationClick(dest)}
                  disabled={!dest.path}
                  className={`px-6 md:px-10 py-3 text-[10px] md:text-xs tracking-[0.25em] uppercase font-label border transition-all duration-700 ${
                    dest.path
                      ? "border-foreground/20 text-foreground hover:bg-foreground hover:text-background cursor-pointer"
                      : "border-foreground/8 text-muted-foreground/30 cursor-default"
                  }`}
                >
                  {dest.label}
                  {!dest.path && <span className="ml-2 text-[8px] tracking-normal normal-case opacity-50">Soon</span>}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opening */}
      <LifestyleOpening />

      {/* Philosophy */}
      <LifestylePhilosophy />

      {/* Your Experience — asymmetric 7/5 layout */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-center">
            <motion.div
              className="md:col-span-7 order-2 md:order-1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.8, ease }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <motion.img
                  src={imgChauffeur}
                  alt="Arzani Lifestyle experience"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  whileInView={{ scale: [1.05, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease }}
                />
              </div>
            </motion.div>
            <motion.div
              className="md:col-span-5 order-1 md:order-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.2, ease }}
            >
              <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">The Arzani Approach</span>
              <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-wide uppercase leading-[1.1] mb-8">
                A Refined Way<br />of Living
              </h2>
              <div className="w-8 h-px bg-bronze/40 mb-8" />
              <p className="font-body text-muted-foreground text-sm leading-[2] mb-6">
                Arzani represents a refined way of living — where time is valued, decisions are simplified, and experiences unfold naturally. We are building a community of individuals who value access, discretion, and a certain standard of living.
              </p>
              <p className="font-body text-muted-foreground text-sm leading-[2]">
                Every arrangement is handled quietly in the background, allowing you to focus entirely on your time and your surroundings.
              </p>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Services */}
      <LifestyleServices />

      {/* Closing */}
      <LifestyleClosing />

      {/* FAQ */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
          >
            <Accordion type="single" collapsible className="w-full">
              {faqs.slice(0, 5).map((item, i) => (
                <AccordionItem key={i} value={`faq-left-${i}`} className="border-foreground/10">
                  <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Accordion type="single" collapsible className="w-full">
              {faqs.slice(5).map((item, i) => (
                <AccordionItem key={i} value={`faq-right-${i}`} className="border-foreground/10">
                  <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <p className="font-body text-muted-foreground/60 text-sm mb-6">Still have a question?</p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground tracking-wide uppercase mb-6">
              Have a specific request?
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm leading-[2] mb-14">
              Our team is available to assist with any arrangement — from dining and transport to bespoke experiences.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 border border-primary-foreground/20 text-primary-foreground/70 font-label tracking-[0.25em] text-[10px] hover:bg-primary-foreground/5 hover:border-primary-foreground/30 transition-all duration-700"
              >
                Contact via WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroup.com"
                className="px-10 py-4 border border-primary-foreground/10 text-primary-foreground/40 font-label tracking-[0.25em] text-[10px] hover:bg-primary-foreground/5 hover:border-primary-foreground/20 transition-all duration-700"
              >
                Send an Email
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Lifestyle;
